import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { XMarkIcon, WalletIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { walletApi, Transaction } from '../services/platformApi';
import { loadRazorpayScript } from '../utils/razorpay';
import { useWalletModal } from '../store/walletModal';
import { env } from '../config/env';

const PRESETS = [500, 1000, 2000];
const PAGE_SIZE = 20;

// PayPal top-ups are no longer offered. The return handler below still
// captures an order a user approved before the option was removed.
const PAYPAL_ENABLED = env.ENABLE_PAYPAL;

function serverMsg(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}

export default function WalletModal() {
  const { open, show, hide } = useWalletModal();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [amount, setAmount] = useState<number>(500);
  const [page, setPage] = useState(1);
  const processedTokenRef = useRef<string | null>(null);

  const walletQ = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getWallet().then((r) => r.data),
    enabled: open,
  });

  const txQ = useQuery({
    queryKey: ['transactions', page],
    queryFn: () => walletApi.getTransactions(page, PAGE_SIZE).then((r) => r.data),
    enabled: open,
  });

  const refreshWallet = () => {
    qc.invalidateQueries({ queryKey: ['wallet'] });
    qc.invalidateQueries({ queryKey: ['transactions'] });
  };

  // ---- Razorpay top-up ----------------------------------------------------
  const topUpM = useMutation({
    mutationFn: (amt: number) => walletApi.topUp(amt).then((r) => r.data),
    onSuccess: async (data) => {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return;
      }
      const options = {
        key: data.key,
        amount: data.amount * 100,
        currency: data.currency,
        name: data.name,
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        handler: async (resp: any) => {
          try {
            await walletApi.verifyPayment(
              resp.razorpay_order_id,
              resp.razorpay_payment_id,
              resp.razorpay_signature
            );
            toast.success('Payment successful!');
            refreshWallet();
          } catch {
            toast.error('Payment verification failed');
          }
        },
        theme: { color: '#F97316' },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (r: any) => {
        toast.error(`Payment failed: ${r.error?.description || 'unknown error'}`);
      });
      razorpay.open();
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Failed to initiate payment')),
  });

  // ---- PayPal return (capture orders approved before PayPal was removed) ----
  const capturePayPalM = useMutation({
    mutationFn: (orderId: string) => walletApi.capturePayPalOrder(orderId).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Payment successful! ₹${data.amount_credited?.toFixed(2) || ''} added to your wallet.`);
      refreshWallet();
    },
    onError: (e: any) => {
      toast.error(serverMsg(e, 'Failed to capture PayPal payment'));
    },
  });

  // Handle the PayPal return redirect (?payment=success&token=<orderId>). This
  // runs on every mount — even when the modal is closed — because PayPal
  // redirects the whole page back here after approval.
  useEffect(() => {
    if (!PAYPAL_ENABLED) return;
    const payment = searchParams.get('payment');
    const token = searchParams.get('token');
    if (payment === 'success' && token) {
      if (processedTokenRef.current === token) return;
      processedTokenRef.current = token;
      show(); // surface the modal so the user sees the result
      setSearchParams({}, { replace: true });
      capturePayPalM.mutate(token, {
        onSettled: () => {
          processedTokenRef.current = null;
        },
      });
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (!open) return null;

  const wallet = walletQ.data;
  const transactions: Transaction[] = txQ.data?.transactions ?? [];
  const total = txQ.data?.total ?? 0;
  const hasNext = page * PAGE_SIZE < total;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={hide}
    >
      <div
        className="card flex max-h-[90vh] w-full max-w-lg flex-col gap-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WalletIcon className="h-5 w-5 text-primary-400" />
            <h2 className="text-lg font-semibold text-white">Wallet</h2>
          </div>
          <button
            onClick={hide}
            className="p-1 text-gray-400 hover:text-white"
            aria-label="Close wallet"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Balance */}
        <div className="pixel-card !p-4">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Available balance</div>
          {walletQ.isLoading ? (
            <div className="mt-1 text-2xl font-bold text-gray-600">…</div>
          ) : walletQ.isError ? (
            <div className="mt-1 text-sm text-red-400">Could not load balance.</div>
          ) : (
            <div className="mt-1 text-3xl font-bold text-white">
              ₹{(wallet?.available_balance ?? wallet?.balance ?? 0).toFixed(2)}
            </div>
          )}
        </div>

        {/* Top up */}
        <div className="flex flex-col gap-3">
          <div className="text-sm font-medium text-gray-300">Add funds</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setAmount(p)}
                className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                  amount === p
                    ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                    : 'border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                ₹{p}
              </button>
            ))}
            <div className="relative flex-1 min-w-[120px]">
              <span className="absolute left-3 top-2 text-sm text-gray-500">₹</span>
              <input
                type="number"
                min={100}
                value={amount}
                onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                className="input !pl-7"
                placeholder="Custom amount"
              />
            </div>
          </div>

          <button
            className="btn-primary w-full"
            disabled={amount < 100 || topUpM.isPending}
            onClick={() => topUpM.mutate(amount)}
          >
            {topUpM.isPending ? 'Processing…' : `Pay ₹${amount} with Razorpay`}
          </button>

          {amount > 0 && amount < 100 && (
            <p className="text-xs text-amber-400">Minimum top-up is ₹100.</p>
          )}
        </div>

        {/* Transactions */}
        <div className="flex flex-col gap-2 border-t border-gray-800 pt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-300">Wallet activity - all products</div>
            {total > 0 && <div className="text-xs text-gray-500">{total} total</div>}
          </div>

          {txQ.isLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">Loading activity…</div>
          ) : txQ.isError ? (
            <div className="py-6 text-center text-sm text-red-400">Could not load transactions.</div>
          ) : transactions.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500">No transactions yet.</div>
          ) : (
            <div className="divide-y divide-gray-800">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">{tx.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(tx.created_at).toLocaleString()} · {tx.type}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 font-mono text-sm font-semibold ${
                      tx.type === 'credit' ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {tx.type === 'credit' ? '+' : '-'}₹{(tx.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-between pt-2">
              <button
                className="btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page}</span>
              <button
                className="btn-secondary btn-sm"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Load more
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
